import React, { useEffect, useCallback } from 'react';
import * as PropTypes from 'prop-types';
import {
  Form, Radio, Slider, Spin, Typography, PageHeader, Row, Button, Result, Switch,
} from 'antd';
import { withRouter } from 'react-router-dom';
import { FormattedMessage, injectIntl } from 'react-intl';
import '../Configure.css';
import { connect } from 'react-redux';
import { selectVideoOptionsPageDataRequest } from '../../../../state/ui/configuration/inspection/videoOptionUiReducer';
import { requestVideoOptionsPageData, requestUpdateVideoOptions } from '../../../../state/ui/configuration/inspection/videoOptionUiActions';
import { selectVideoOptions } from '../../../../state/domain/videoOption/videoOptionReducer';
import { selectAppConfigMaxRange } from '../../../../state/domain/appConfig/appConfigReducer';
import { videoOptionsUploadPreference } from './config-util';
import { selectSessionUser } from '../../../../state/ui/application/sessionUiReducer';
import { userIsAdmin } from '../company/user-util';
import '../company/ContactDetails.css';
import routeLocations from '../../../../utility/route-locations';


const MaxVideosToUploadField = ({ videoOptions, form, appConfigMaxRange }) => {
  const maxRange = appConfigMaxRange ? appConfigMaxRange.applicationConfigValue : 0;
  const midRange = Math.ceil(maxRange / 2);

  // ticks to depict the starting/middle/end points on the Slider
  const tickMarkings = {
    0: {
      style: { color: 'var(--component-primary-colour)' },
      label: <strong>0</strong>,
    },
    [midRange]: {
      style: { color: 'var(--component-primary-colour)' },
      label: <strong>{midRange}</strong>,
    },
    [maxRange]: {
      style: { color: 'var(--component-primary-colour)' },
      label: <strong>{maxRange}</strong>,
    },
  };

  return (
    <Form.Item className="contact-details-content-row" label={<FormattedMessage id="maxNoVideos" />}>
      { form.getFieldDecorator('videosAllowed', {
        initialValue: videoOptions.videosAllowed,
      })(
        <Slider
          style={{ maxWidth: '380px' }}
          marks={tickMarkings}
          max={Number(appConfigMaxRange.applicationConfigValue)}
        />,
      )}
    </Form.Item>
  );
};

MaxVideosToUploadField.propTypes = {
  videoOptions: PropTypes.shape({
    videosAllowed: PropTypes.number,
    restrictMediaUploadsToWifi: PropTypes.bool,
  }),
  appConfigMaxRange: PropTypes.shape({
    applicationConfigValue: PropTypes.string,
  }),
  form: PropTypes.shape({
    validateFields: PropTypes.func.isRequired,
    getFieldDecorator: PropTypes.func.isRequired,
  }).isRequired,
};

MaxVideosToUploadField.defaultProps = {
  videoOptions: null,
  appConfigMaxRange: null,
};

const UploadPreferenceField = injectIntl(({
  videoOptions, form,
}) => (
  <Form.Item className="contact-details-content-row" label={<FormattedMessage id="uploadPref" />}>
    { form.getFieldDecorator('restrictMediaUploadsToWifi', {
      initialValue: videoOptions.restrictMediaUploadsToWifi,
    })(
      <Radio.Group buttonStyle="solid">
        <Radio.Button value={videoOptionsUploadPreference.WIFI_ONLY}><FormattedMessage id="wiFiOnly" /></Radio.Button>
        <Radio.Button value={videoOptionsUploadPreference.WIFI_AND_MOBILE}><FormattedMessage id="wiFiAndMobile" /></Radio.Button>
      </Radio.Group>,
    )}
  </Form.Item>
));

UploadPreferenceField.propTypes = {
  videoOptions: PropTypes.shape({
    videosAllowed: PropTypes.number,
    restrictMediaUploadsToWifi: PropTypes.bool,
  }),
  form: PropTypes.shape({
    validateFields: PropTypes.func.isRequired,
    getFieldDecorator: PropTypes.func.isRequired,
  }).isRequired,
};

UploadPreferenceField.defaultProps = {
  videoOptions: null,
};

const DamageImagesMandatoryField = injectIntl(({ videoOptions, form }) => (
    <Form.Item label={<FormattedMessage id="damageImagesMandatory" />}>
      { form.getFieldDecorator('damageImagesMandatory', {
        initialValue: videoOptions ? videoOptions.damageImagesMandatory : false,
      })(
          <Switch
              checked={form.getFieldValue('damageImagesMandatory')}
              onChange={(val) => {
                // eslint-disable-next-line no-param-reassign
                form.setFieldsValue({ damageImagesMandatory: val });
                return val;
              }}
          />,
      )}
    </Form.Item>
));

// MAIN COMPONENT
function EditVideoOptions({
  videoOptions, loadPageData, onUpdate, appConfigMaxRange, history, sessionUser, form, wizard, wizardNext,
}) {
  useEffect(() => {
    loadPageData();
  }, [loadPageData]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    form.validateFields((err, values) => {
      if (!err) {
        onUpdate({ ...values }, history, wizard, wizardNext);
      }
    });
  }, [history, form, onUpdate, wizard, wizardNext]);

  if (!userIsAdmin(sessionUser)) {
    return (
      <Result
        status="403"
        title={<FormattedMessage id="accessRestricted" />}
        subTitle={<FormattedMessage id="insufficientPermissions" />}
      />
    );
  }

  return (
    <Form onSubmit={e => handleSubmit(e)}>
      <PageHeader
        style={{ display: wizard ? 'none' : '', width: '100%' }}
        onBack={() => history.push(routeLocations.configure_video_options)}
        title={(<Typography.Title level={3}><FormattedMessage id="videoOptions" /></Typography.Title>)}
      />
      <div style={{ padding: wizard ? '0' : '0 1em' }}>
        {
          (!videoOptions || !appConfigMaxRange) ? <Spin />
            : <MaxVideosToUploadField videoOptions={videoOptions} appConfigMaxRange={appConfigMaxRange} form={form} />
        }
        { (!videoOptions) ? <Spin /> :
            <div>
              <UploadPreferenceField videoOptions={videoOptions} form={form} />
              <DamageImagesMandatoryField videoOptions={videoOptions} form={form} />
            </div> }
        <Form.Item>
          <Row type="flex" justify="end" style={{ marginTop: '0.5em', marginRight: '2em' }}>
            <Button loading={false} type="link" style={{ display: wizard ? '' : 'none', marginRight: '0.5em' }} onClick={() => wizardNext(true)}>
              <FormattedMessage id="configureLater" />
            </Button>
            <Button loading={false} type="primary" htmlType="submit"><FormattedMessage id="save" /></Button>
          </Row>
        </Form.Item>
      </div>
    </Form>
  );
}

EditVideoOptions.propTypes = {
  loadPageData: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  videoOptions: PropTypes.shape({
    videosAllowed: PropTypes.number,
    restrictMediaUploadsToWifi: PropTypes.bool,
    damageImagesMandatory: PropTypes.bool,
  }),
  appConfigMaxRange: PropTypes.shape({
    applicationConfigValue: PropTypes.string,
  }),
  sessionUser: PropTypes.shape({
    linkedRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  history:
    PropTypes.shape({
      push: PropTypes.func.isRequired,
      goBack: PropTypes.func.isRequired,
    }).isRequired,
  form: PropTypes.shape({
    validateFields: PropTypes.func.isRequired,
    getFieldDecorator: PropTypes.func.isRequired,
  }).isRequired,
  wizard: PropTypes.bool,
  wizardNext: PropTypes.func,
};

EditVideoOptions.defaultProps = {
  videoOptions: null,
  appConfigMaxRange: null,
  wizard: false,
  wizardNext: () => {},
};

/**
 * A function for connecting redux state to the component props.
 */
const mapStateToProps = (state) => {
  const pageDataRequest = selectVideoOptionsPageDataRequest(state);
  const appConfigMaxRange = selectAppConfigMaxRange(state);

  return {
    error: pageDataRequest ? pageDataRequest.error : null,
    videoOptions: selectVideoOptions(state),
    appConfigMaxRange,
    sessionUser: selectSessionUser(state),
  };
};

/**
 * A function for connecting redux action to the component props.
 */
const mapDispatchToProps = dispatch => ({
  loadPageData: () => dispatch(requestVideoOptionsPageData()),
  onUpdate: (newVideoOptions, history, wizard, wizardNext) => dispatch(requestUpdateVideoOptions(newVideoOptions, history, wizard, wizardNext)),
});

/**
 * Connect the redux state to our component.
 */
export default connect(mapStateToProps, mapDispatchToProps)(withRouter(
  Form.create({ name: 'edit_contact_details' })(EditVideoOptions),
));
